import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  ArrowUpRight,
  Bookmark,
  Clock,
  MapPin,
  Navigation,
  Pause,
  Play,
  RotateCcw,
  X,
} from "lucide-react";
import AnimatedStreet from "./AnimatedStreet";
import VideoStreet, { VIDEO_CROSSFADE_MS } from "./VideoStreet";
import MobileRideVideo from "./MobileRideVideo";
import { cruise, discoveries, mobileRideVideos } from "./discoveries";
import opening from "../assets/ride/generated/anvesho-home-hero-driver-identity-02.webp";
import "./AutoRide.css";

const INITIAL_CRUISE_SECONDS = 3.6;
const COMPLETE_CRUISE_SECONDS = 2.8;
const POST_START_TRANSITION_MS = 1200;

export default function AutoRide() {
  const motionTest = new URLSearchParams(window.location.search).has("motionTest");
  const canvasStreet = new URLSearchParams(window.location.search).has("canvasStreet");
  const mobileRide = window.matchMedia("(max-width: 600px)").matches;
  const cruiseVideo = mobileRide ? cruise.mobileVideo : cruise.video;
  const cruisePoster = mobileRide ? cruise.mobilePoster : null;
  const [state, setState] = useState("WAITING");
  const [paused, setPaused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoSrc, setVideoSrc] = useState(cruiseVideo);
  const [videoPoster, setVideoPoster] = useState(cruisePoster);
  const [videoStartAt, setVideoStartAt] = useState(0);
  const [savedStops, setSavedStops] = useState([]);
  const [seenStops, setSeenStops] = useState([]);
  const [driverLine, setDriverLine] = useState("");
  const [cruiseOffset, setCruiseOffset] = useState(0);
  const [showStops, setShowStops] = useState(false);
  const [firstVideoReady, setFirstVideoReady] = useState(false);
  const [videoPlaybackStarted, setVideoPlaybackStarted] = useState(false);
  const reduce = useReducedMotion();
  const startButton = useRef(null);
  const stopButton = useRef(null);
  const continueButton = useRef(null);
  const videoStreet = useRef(null);
  const stateTimer = useRef(null);
  const riding = state !== "WAITING";
  const activeDiscovery = discoveries[activeIndex] || discoveries[discoveries.length - 1];
  const activeDiscoveryVideo = mobileRide ? activeDiscovery.mobileVideo : activeDiscovery.video;
  const activeDiscoveryPoster = mobileRide ? activeDiscovery.mobilePoster : null;
  const activeDiscoveryStart = mobileRide ? activeDiscovery.mobileStart : 0;
  const openingVisible = !riding || (!firstVideoReady && !reduce);

  const routeStops = useMemo(() => discoveries.map((item, index) => ({
    id: item.id,
    reached: index < activeIndex || seenStops.includes(item.id),
  })), [activeIndex, seenStops]);

  const preloadSources = useMemo(() => {
    if (mobileRide) return mobileRideVideos;
    const nextDiscovery = discoveries[activeIndex + 1];
    const nextVideo = nextDiscovery && (mobileRide ? nextDiscovery.mobileVideo : nextDiscovery.video);
    if (!riding || state === "STARTING") return [cruiseVideo, activeDiscoveryVideo];
    const upcoming = [cruiseVideo, activeDiscoveryVideo, nextVideo].filter(Boolean);
    return [...new Set(upcoming)];
  }, [activeDiscoveryVideo, activeIndex, cruiseVideo, mobileRide, riding, state]);

  useEffect(() => {
    document.title = "Anvesho | Hop on the auto";
    document.body.classList.add("ride-active");
    return () => document.body.classList.remove("ride-active");
  }, []);

  useEffect(() => () => {
    clearRideTimer();
  }, []);

  useEffect(() => {
    if (state === "DISCOVERY_NOTICE") stopButton.current?.focus({ preventScroll: true });
    if (state === "STOPPED") continueButton.current?.focus({ preventScroll: true });
  }, [state]);

  useEffect(() => {
    if (paused || state === "WAITING" || state === "STOPPED" || state === "COMPLETE") return;
    if (!canvasStreet && !videoPlaybackStarted) return;
    clearRideTimer();

    if (state === "STARTING") {
      stateTimer.current = window.setTimeout(() => beginCruise(INITIAL_CRUISE_SECONDS), reduce ? 120 : POST_START_TRANSITION_MS);
    }

    if (mobileRide && !canvasStreet) return clearRideTimer;

    if (state === "CRUISING") {
      const delay = activeIndex === 0 ? INITIAL_CRUISE_SECONDS : discoveries[Math.max(activeIndex - 1, 0)]?.cruiseAfter || 4;
      stateTimer.current = window.setTimeout(startDiscoveryApproach, delay * 1000);
    }

    if (state === "DISCOVERY_APPROACH") {
      stateTimer.current = window.setTimeout(showNotice, activeDiscovery.noticeAt * 1000);
    }

    if (state === "TRANSITION") {
      stateTimer.current = window.setTimeout(() => {
        if (activeIndex >= discoveries.length) {
          setState("COMPLETE");
          trackRide("ride_completed");
        } else {
          setState("CRUISING");
        }
      }, COMPLETE_CRUISE_SECONDS * 1000);
    }

    return clearRideTimer;
    // Timers are intentionally keyed by ride state to avoid stale scheduled transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, paused, activeIndex, activeDiscovery, reduce, canvasStreet, videoPlaybackStarted, mobileRide]);

  useEffect(() => {
    function onVisibilityChange() {
      if (document.hidden && riding && state !== "COMPLETE") {
        trackRide("ride_abandoned", { lastDiscoveryId: seenStops.at(-1) || null });
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [riding, state, seenStops]);

  function clearRideTimer() {
    if (stateTimer.current) {
      window.clearTimeout(stateTimer.current);
      stateTimer.current = null;
    }
  }

  function beginRide() {
    setVideoPlaybackStarted(false);
    videoStreet.current?.startFromGesture().catch(() => {});
    startRideTransition();
  }

  function startRideTransition() {
    clearRideTimer();
    setActiveIndex(0);
    setSavedStops([]);
    setSeenStops([]);
    setDriverLine("");
    setVideoSrc(cruiseVideo);
    setVideoPoster(cruisePoster);
    setVideoStartAt(0);
    setCruiseOffset(0);
    setShowStops(false);
    setPaused(false);
    setState("STARTING");
    trackRide("ride_started");
  }

  function beginCruise(delay = INITIAL_CRUISE_SECONDS) {
    setVideoSrc(cruiseVideo);
    setVideoPoster(cruisePoster);
    if (!mobileRide) setVideoStartAt(cruiseOffset);
    setState("CRUISING");
    if (delay !== INITIAL_CRUISE_SECONDS) {
      clearRideTimer();
      stateTimer.current = window.setTimeout(startDiscoveryApproach, delay * 1000);
    }
  }

  function startDiscoveryApproach() {
    if (!activeDiscovery) return;
    setVideoSrc(activeDiscoveryVideo);
    setVideoPoster(activeDiscoveryPoster);
    setVideoStartAt(activeDiscoveryStart);
    setState("DISCOVERY_APPROACH");
  }

  function showNotice() {
    setSeenStops((items) => items.includes(activeDiscovery.id) ? items : [...items, activeDiscovery.id]);
    setDriverLine(activeDiscovery.driverLine || "");
    setState("DISCOVERY_NOTICE");
    trackRide("discovery_seen", { discoveryId: activeDiscovery.id });
  }

  function keepRiding(source = "notice") {
    if (mobileRide) videoStreet.current?.resumeNow().catch(() => {});
    clearRideTimer();
    const discoveryId = activeDiscovery.id;
    trackRide(source === "detail" ? "continue_ride_clicked" : "keep_riding_clicked", { discoveryId });
    const nextIndex = activeIndex + 1;
    setCruiseOffset(activeDiscovery.cruiseOffset || 0);
    setDriverLine(discoveryId === "live-music" ? "Long way it is." : "");
    setActiveIndex(nextIndex);

    if (!mobileRide) {
      setVideoSrc(cruiseVideo);
      setVideoPoster(cruisePoster);
      setVideoStartAt(activeDiscovery.cruiseOffset || 0);
    }
    setState(nextIndex >= discoveries.length ? "TRANSITION" : "CRUISING");
  }

  function stopHere() {
    clearRideTimer();
    videoStreet.current?.pauseNow();
    setState("STOPPED");
    trackRide("stop_here_clicked", { discoveryId: activeDiscovery.id });
    trackRide("detail_opened", { discoveryId: activeDiscovery.id });
  }

  function saveStop() {
    setSavedStops((items) => items.includes(activeDiscovery.id) ? items : [...items, activeDiscovery.id]);
  }

  function reset() {
    if (mobileRide) videoStreet.current?.reset();
    clearRideTimer();
    setState("WAITING");
    setPaused(false);
    setActiveIndex(0);
    setVideoSrc(cruiseVideo);
    setVideoPoster(cruisePoster);
    setVideoStartAt(0);
    setSavedStops([]);
    setSeenStops([]);
    setDriverLine("");
    setCruiseOffset(0);
    setShowStops(false);
    requestAnimationFrame(() => startButton.current?.focus());
  }

  return (
    <div className={`auto-ride ${riding ? "is-riding" : "is-waiting"} is-${state.toLowerCase()}`}>
      {canvasStreet ? (
        <AnimatedStreet
          motionTest={motionTest}
          state={state}
          paused={paused}
          reducedMotion={!!reduce}
          onApproach={() => setState("DISCOVERY_APPROACH")}
          onEncounter={showNotice}
          onComplete={() => setState("COMPLETE")}
        />
      ) : mobileRide ? (
        <MobileRideVideo ref={videoStreet} src={cruiseVideo}
          onReady={() => setFirstVideoReady(true)}
          onPlaying={() => setVideoPlaybackStarted(true)}
          onNotice={(index) => {
            const discovery = discoveries[index];
            setActiveIndex(index);
            setSeenStops((items) => items.includes(discovery.id) ? items : [...items, discovery.id]);
            setDriverLine(discovery.driverLine || "");
            setState("DISCOVERY_NOTICE");
          }}
          onComplete={() => { setState("COMPLETE"); trackRide("ride_completed"); }}
        />
      ) : (
        <VideoStreet
          ref={videoStreet}
          src={videoSrc}
          poster={videoPoster}
          preloadSources={preloadSources}
          state={state}
          paused={paused}
          reducedMotion={!!reduce}
          isCruise={videoSrc === cruiseVideo}
          startAt={mobileRide ? videoStartAt : videoSrc === cruiseVideo ? cruiseOffset : 0}
          onReady={() => setFirstVideoReady(true)}
          onPlaying={() => setVideoPlaybackStarted(true)}
        />
      )}

      <motion.div
        className="ride-opening"
        initial={false}
        animate={{ opacity: openingVisible ? 1 : 0, scale: state === "STARTING" && !reduce ? 1.035 : 1 }}
        transition={{ duration: reduce ? 0 : state === "STARTING" ? 1.05 : .35, ease: [0.4, 0, 0.2, 1] }}
        style={{ backgroundImage: `url(${opening})`, pointerEvents: "none", containerType: "size" }}
      >
      </motion.div>

      <div className="ride-shade" aria-hidden="true" />
      <div className="ride-topbar">
        <button className="ride-brand" onClick={reset} aria-label="Anvesho, return to waiting auto">anvesho<span>.</span></button>
        <span className="ride-tagline">Explore · Eat · Experience</span>
        <div className="ride-location"><MapPin size={15} /><span>Whitefield, Bengaluru</span><span className="ride-location-dot" /></div>
      </div>

      <AnimatePresence>
        {!riding && (
          <motion.main key="welcome" className="ride-welcome" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: reduce ? 0 : -8 }} transition={{ duration: reduce ? 0 : .3 }}>
            <p className="ride-eyebrow"><span /> No plans? Perfect.</p>
            <h1>Hop on<br />the <em>Auto!</em></h1>
            <p className="ride-intro">Let's take the long way<br />through Whitefield.</p>
            <button ref={startButton} className="ride-cta" onClick={beginRide}>
              Start ride <ArrowRight size={22} />
            </button>
            <p className="ride-helper">A playful way to discover what's around you.</p>
          </motion.main>
        )}
      </AnimatePresence>

      {riding && (
        <div className="ride-controls">
          <div className="ride-route" aria-label="Whitefield ride progress">
            <span className="route-label">Whitefield ride</span>
            <div className="route-dots">
              {routeStops.map((stop) => <i key={stop.id} className={stop.reached ? "is-reached" : ""} />)}
            </div>
          </div>
          <div className="ride-secondary-controls">
            <button className="ride-icon-button" title={paused ? "Resume ride" : "Pause ride"} aria-label={paused ? "Resume ride" : "Pause ride"} onClick={() => {
              if (mobileRide) videoStreet.current?.togglePause(!paused)?.catch(() => {});
              setPaused(!paused);
            }}>{paused ? <Play size={18} /> : <Pause size={18} />}</button>
            <button className="ride-icon-button" title="Restart ride" aria-label="Restart ride" onClick={reset}><RotateCcw size={18} /></button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {driverLine && (state === "DISCOVERY_NOTICE" || state === "CRUISING") && (
          <motion.div className="driver-bubble" initial={{ opacity: 0, y: reduce ? 0 : 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduce ? 0 : 4 }}>
            <small>Driver</small>{driverLine}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {state === "DISCOVERY_NOTICE" && (
          <NoticeCard discovery={activeDiscovery} onStop={stopHere} onContinue={() => keepRiding("notice")} stopRef={stopButton} reduce={reduce} />
        )}
        {state === "STOPPED" && (
          <DetailCard discovery={activeDiscovery} onContinue={() => keepRiding("detail")} onSave={saveStop} saved={savedStops.includes(activeDiscovery.id)} continueRef={continueButton} reduce={reduce} />
        )}
        {state === "COMPLETE" && (
          <RideComplete savedStops={savedStops} showStops={showStops} onViewStops={() => setShowStops(true)} onReset={reset} reduce={reduce} />
        )}
      </AnimatePresence>

      <div className="ride-bottom">
        {riding && <span className="ride-status" role="status">{getStatusText(state, activeDiscovery, activeIndex)}</span>}
      </div>
    </div>
  );
}

function NoticeCard({ discovery, onStop, onContinue, stopRef, reduce }) {
  return (
    <motion.section
      key={discovery.id}
      className="ride-encounter"
      aria-labelledby="encounter-heading"
      initial={{ opacity: 0, y: reduce ? 0 : 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reduce ? 0 : 6 }}
      transition={{ duration: .3, ease: [0.22, 1, 0.36, 1] }}
    >
      <p className="encounter-kicker"><span>{discovery.icon}</span> {discovery.eyebrow}</p>
      <h2 id="encounter-heading">{discovery.title}</h2>
      <p className="encounter-meta">{discovery.meta}</p>
      <div className="discovery-actions">
        <button ref={stopRef} className="ride-cta" onClick={onStop}>Stop here <ArrowUpRight size={17} /></button>
        <button className="ride-text-button" onClick={onContinue}>Keep riding <ArrowRight size={16} /></button>
      </div>
    </motion.section>
  );
}

function DetailCard({ discovery, onContinue, onSave, saved, continueRef, reduce }) {
  return (
    <motion.section
      key={`${discovery.id}-detail`}
      className="ride-discovery is-stopped"
      aria-labelledby="discovery-heading"
      initial={{ opacity: 0, y: reduce ? 0 : 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reduce ? 0 : 12 }}
      transition={{ duration: .4 }}
    >
      <div className="discovery-top">
        <span><span>{discovery.icon}</span> {discovery.eyebrow}</span>
        <button aria-label="Close discovery and continue ride" onClick={onContinue}><X size={18} /></button>
      </div>
      {discovery.poster ? (
        <div className="discovery-poster image-poster"><img src={discovery.poster} alt={discovery.posterAlt} /></div>
      ) : (
        <div className={`discovery-poster type-poster poster-${discovery.id}`}>
          <span>{discovery.detailTitle}</span>
          <small>{discovery.category} · {discovery.distance}</small>
        </div>
      )}
      <div className="discovery-content">
        <p className="discovery-category">{discovery.category} <span>Prototype stop</span></p>
        <h2 id="discovery-heading">{discovery.detailTitle}</h2>
        <div className="discovery-facts">
          <span><Clock size={13} />{discovery.time}</span>
          <span><MapPin size={13} />{discovery.distance}</span>
        </div>
        <p>{discovery.shortDescription}</p>
        <div className="discovery-extra">
          <h3>Why pull over?</h3>
          <p>{discovery.note}</p>
          <strong>{discovery.price}</strong>
          <small>Prototype behaviour. Directions are not connected yet.</small>
        </div>
        <div className="discovery-actions">
          <button className="ride-small-action" onClick={onSave}><Bookmark size={15} />{saved ? "Saved" : "Save"}</button>
          <button className="ride-small-action"><Navigation size={15} />Directions</button>
          <button ref={continueRef} className="ride-cta" onClick={onContinue}>Continue ride <ArrowRight size={18} /></button>
        </div>
      </div>
    </motion.section>
  );
}

function RideComplete({ savedStops, showStops, onViewStops, onReset, reduce }) {
  return (
    <motion.section
      className="ride-complete"
      initial={{ opacity: 0, y: reduce ? 0 : 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: .4 }}
    >
      <p className="encounter-kicker">The long way home</p>
      <h2>You found 5 things around Whitefield.</h2>
      <div className="ride-recap">
        {discoveries.map((item) => <span key={item.id} className={savedStops.includes(item.id) ? "is-saved" : ""}>{item.icon} {item.type}</span>)}
      </div>
      {showStops && (
        <div className="ride-stops-detail">
          {discoveries.map((item) => (
            <article key={item.id}>
              <strong>{item.detailTitle}</strong>
              <small>{item.meta}</small>
            </article>
          ))}
        </div>
      )}
      <div className="discovery-actions">
        <button className="ride-cta" onClick={onReset}>Ride again <RotateCcw size={17} /></button>
        <button className="ride-text-button" onClick={onViewStops}>View my stops <ArrowRight size={16} /></button>
      </div>
    </motion.section>
  );
}

function getStatusText(state, discovery, activeIndex) {
  if (state === "COMPLETE") return "Ride complete";
  if (state === "STOPPED") return "Pulled over";
  if (state === "DISCOVERY_NOTICE") return `${discovery.type} nearby`;
  if (state === "DISCOVERY_APPROACH") return "Something coming up";
  if (activeIndex > 1) return `Stop ${activeIndex + 1} of 5 is somewhere ahead`;
  return "Whitefield ride";
}

function trackRide(eventName, payload = {}) {
  const event = {
    event: eventName,
    ...payload,
    at: new Date().toISOString(),
  };
  window.dispatchEvent(new CustomEvent("anvesho:ride-event", { detail: event }));
  console.info("[Anvesho ride]", event);
  try {
    const key = "anveshoRideEvents";
    const existing = JSON.parse(window.localStorage.getItem(key) || "[]");
    window.localStorage.setItem(key, JSON.stringify([...existing, event].slice(-200)));
  } catch {
    // Local analytics should never interrupt the ride.
  }
}

export const rideTuning = {
  crossfadeMs: VIDEO_CROSSFADE_MS,
  initialCruiseSeconds: INITIAL_CRUISE_SECONDS,
  completeCruiseSeconds: COMPLETE_CRUISE_SECONDS,
  postStartTransitionMs: POST_START_TRANSITION_MS,
};
